import { useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Button } from "@/components/ui/button";
import { Camera as CameraIcon, Image as ImageIcon } from "lucide-react";

export interface CapturedPhoto {
  dataUrl: string;
  width: number;
  height: number;
}

interface Props {
  onCaptured: (photo: CapturedPhoto) => void;
  label?: string;
  disabled?: boolean;
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// Resize a data URL to a max long-edge pixel count and re-encode as JPEG.
// Modern phone JPEGs can be 10+ MB, which blows past GAE's 32 MB request
// size limit once base64-encoded. Downscaling to 1600px also keeps vision
// latency reasonable without losing the detail the model needs.
const MAX_LONG_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

async function resizeDataUrl(
  input: string,
): Promise<{ dataUrl: string; width: number; height: number }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("Failed to load image"));
    el.src = input;
  });
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  const longEdge = Math.max(srcW, srcH);
  const scale = longEdge > MAX_LONG_EDGE_PX ? MAX_LONG_EDGE_PX / longEdge : 1;
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));
  if (scale === 1 && input.startsWith("data:image/jpeg")) {
    return { dataUrl: input, width: w, height: h };
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { dataUrl: input, width: srcW, height: srcH };
  ctx.drawImage(img, 0, 0, w, h);
  const out = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  return { dataUrl: out, width: w, height: h };
}

// Static import of @capacitor/camera. A previous version dynamically imported
// it to keep the chunk out of the main bundle, but Vite splits dynamic imports
// into their own hashed chunk — and after every App Engine redeploy, in-flight
// WebView sessions still reference the OLD chunk hash. Tapping the button then
// fails with "Failed to fetch dynamically imported module: .../assets/index-*.js".
// Static imports fold the code into the main entry chunk, so there's no separate
// fetch to 404. Shells without the plugin registered still work — the JS module
// imports cleanly; only `Camera.getPhoto()` throws PluginNotImplemented, which
// we catch below.
async function tryNativeGetPhoto(source: "camera" | "photos"): Promise<string | null> {
  if (!Capacitor.isNativePlatform?.()) return null;
  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      resultType: CameraResultType.DataUrl,
      source: source === "camera" ? CameraSource.Camera : CameraSource.Photos,
      correctOrientation: true,
      // Resize natively before handing the data URL to JS. Without this,
      // 48-50MP phones (Z Fold, Pixel Pro, recent Samsungs) deliver
      // ~10MB JPEGs that, when decoded by `new Image()` for the JS-side
      // resize, allocate a 200MB raw bitmap and OOM the WebView renderer.
      // Capacitor's native side downscales on the GPU/Bitmap APIs and
      // skips that decode entirely.
      width: 1600,
      height: 1600,
    });
    return photo.dataUrl ?? null;
  } catch (err: any) {
    const code = err?.code ?? "";
    const message = String(err?.message ?? err ?? "");
    if (code === "UNIMPLEMENTED" || /not implemented|unimplemented/i.test(message)) {
      return null;
    }
    throw err;
  }
}

export function GardenCameraCapture({ onCaptured, label, disabled }: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const fileLibraryRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNative = Capacitor.isNativePlatform?.() ?? false;
  // Detect synchronously whether the native camera plugin is registered in
  // this shell. The current Android APK ships WITHOUT @capacitor/camera, so
  // Camera.getPhoto throws PluginNotImplemented and we have to fall back to
  // <input type="file">. That fallback only works if the input.click() fires
  // inside the same synchronous user-gesture as the tap — Chromium WebView
  // refuses to open a file chooser after an `await`. Branching on this flag
  // at render time lets the onClick handlers be synchronous in that case.
  const cameraPluginAvailable =
    isNative && (Capacitor.isPluginAvailable?.("Camera") ?? false);

  const launchNativeCamera = async (source: "camera" | "photos") => {
    setBusy(true);
    setError(null);
    try {
      const raw = await tryNativeGetPhoto(source);
      if (!raw) {
        setError("Photo picker isn't available on this device.");
        return;
      }
      const resized = await resizeDataUrl(raw);
      onCaptured(resized);
    } catch (err: any) {
      const message = err?.message ?? String(err);
      if (!message.toLowerCase().includes("user cancelled")) {
        setError(message || "Camera failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const raw = await fileToDataUrl(file);
      const resized = await resizeDataUrl(raw);
      onCaptured(resized);
    } catch (err: any) {
      setError(err?.message ?? "Could not read photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label ? <div className="text-sm text-muted-foreground">{label}</div> : null}
      <div className="flex gap-2">
        {cameraPluginAvailable ? (
          <>
            <Button
              type="button"
              variant="default"
              disabled={busy || disabled}
              onClick={() => void launchNativeCamera("camera")}
            >
              <CameraIcon className="mr-2 h-4 w-4" />
              Take photo
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy || disabled}
              onClick={() => void launchNativeCamera("photos")}
            >
              <ImageIcon className="mr-2 h-4 w-4" />
              From library
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="default"
              disabled={busy || disabled}
              onClick={() => fileRef.current?.click()}
            >
              <CameraIcon className="mr-2 h-4 w-4" />
              Take photo
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy || disabled}
              onClick={() => fileLibraryRef.current?.click()}
            >
              <ImageIcon className="mr-2 h-4 w-4" />
              From library
            </Button>
          </>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => void handleFileChange(e)}
      />
      <input
        ref={fileLibraryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void handleFileChange(e)}
      />
      {busy ? <div className="text-xs text-muted-foreground">Working…</div> : null}
      {error ? <div className="text-xs text-destructive">{error}</div> : null}
    </div>
  );
}
